import { useTranslation } from "react-i18next";
import type { TransportAllocationState, TransportDisplayStatus, TransportRequestSource } from "@seagames/contracts";
import { DirectionIcon } from "../travel-leg-form";
import { RouteIcon } from "./icons";
import "./transport.css";

/**
 * A trip's DERIVED status (X6: computed in SQL from its allocations, never
 * stored): her `.cst` chip — a dot and the label in the status colour
 * (`transport.css`). `title` carries the one-line meaning.
 */
export function TripStatusChip({ status, className = "" }: { status: TransportDisplayStatus; className?: string }) {
	const { t } = useTranslation("transport");
	return (
		<span className={`tp-st ${className}`} data-tp-status={status} title={t(`status.hint.${status}`)}>
			{t(`status.label.${status}`)}
		</span>
	);
}

/** The coloured dot alone (status chips in the list toolbar, legends). */
export function TripStatusDot({ status }: { status: TransportDisplayStatus }) {
	return <span aria-hidden className="tp-dot" data-tp-status={status} />;
}

const ALLOCATION_TONE: Record<TransportAllocationState, TransportDisplayStatus> = {
	ASSIGNED: "ASSIGNED",
	EN_ROUTE: "EN_ROUTE",
	DONE: "COMPLETED",
	CANCELLED: "CANCELLED",
};

/** One vehicle's state on a trip: Assigned · En route · Arrived · Cancelled (her allocation chip colours). */
export function AllocationStateChip({ state, className = "" }: { state: TransportAllocationState; className?: string }) {
	const { t } = useTranslation("transport");
	return (
		<span className={`tp-st ${className}`} data-tp-status={ALLOCATION_TONE[state]}>
			{t(`allocationState.${state}`)}
		</span>
	);
}

/**
 * Where a trip came from (her `srcTag` pill): an airport transfer in or out
 * (ARR / DEP, from travel) or a venue trip (ADHOC).
 */
export function TripSourceTag({ source, className = "" }: { source: TransportRequestSource; className?: string }) {
	const { t } = useTranslation("transport");
	return (
		<span
			className={`inline-flex h-[1.25rem] shrink-0 items-center gap-[0.25rem] rounded-[0.25rem] bg-gms-navy-soft px-[0.375rem] text-[0.6875rem] font-semibold whitespace-nowrap text-gms-navy ${className}`}
		>
			{source === "ADHOC" ? (
				<RouteIcon className="size-[0.75rem]" />
			) : (
				<DirectionIcon direction={source === "ARR" ? "ARRIVAL" : "DEPARTURE"} className="size-[0.75rem]" />
			)}
			{t(`source.${source}`)}
		</span>
	);
}
